
import formatDateBasedTimezoneValue from "@/app/infrastructures/misc/common-library/FormatDateBasedTimezoneValue";
import {
  formatDateNumber,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ManagementLoginApprovalController } from "@/app/ui/controllers/ManagementLoginApprovalController";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { LoginApproval, LoginApprovalDeleteReviewBody, LoginApprovalDevice, LoginApprovalDeviceRequest, LoginApprovalHistory, LoginApprovalHistoryRequest, LoginApprovalRequest } from "@/domain/entities/ManagementLoginApproval";
import { Pagination } from "@/domain/entities/Pagination";
import { AxiosError } from "axios";
import { defineComponent, onMounted, ref, watch } from "vue";
import ApprovalResultDefault from "./approval-result-default.vue";
import ApprovalResultNotFound from "./approval-result-not-found.vue";

export default defineComponent({
  name: "ManagementLoginDevice",
  components: {
    ApprovalResultDefault,
    ApprovalResultNotFound,
  },
  setup() {
    // --- search --- ///
    const search = ref("");
    const onSearch = (value: string) => {
      search.value = value;
    };
    const clearSearch = () => {
      search.value = "";
    };

    // --- date --- //
    const startDate = ref("");
    const endDate = ref("");

    const getFormattedDate = (date?: string | Date) => {
      const d = date ? new Date(date) : new Date();
      return formatDateNumber(d);
    };

    const setDateRange = (value: Array<string>) => {
      startDate.value = getFormattedDate(value[0]);
      endDate.value = getFormattedDate(value[1]);
    };

    // --- data --- //
    const pagination = ref(new Pagination(1, 10));
    const isLoading = ref(false);
    const errorCause = ref("");
    const loginApprovalDevicesList = ref<LoginApprovalDevice[]>([]);

    const fetchLoginApproval = () => {
      isLoading.value = true;
      errorCause.value = "";
      const params = {
        query: search.value,
        page: pagination.value.page,
        limit: pagination.value.limit,
        startDate: startDate.value,
        endDate: endDate.value
      }

      ManagementLoginApprovalController.getLoginDevicesList(
        new LoginApprovalDeviceRequest(params)
      )
        .then((res) => {
          loginApprovalDevicesList.value = res.data;
          pagination.value = res.pagination;
          ManagementLoginApprovalController.setRefreshList(false);
        })
        .catch((err) => {
          errorCause.value = parsingErrorResponse(err).type;
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    // --- table modal --- //
    
    const confirmRequestById = async (item: LoginApprovalDevice, reviewType: "approve" | "reject") => {
      try {
        MainAppController.showLoading();
        await ManagementLoginApprovalController.postLoginApprovalDeviceDelete(new LoginApprovalDeleteReviewBody({
          deviceAuthId: item.deviceAuthId,
        })).then(() => {
          const message = `
            <div style="color: #00875A">
              <b>Perangkat berhasil dihapus</b><br />
              <b>${item.accountName}</b> telah dihapus dari perangkat terdaftar.
            </div>
          `
          
          MainAppController.setSnackbarMessage(message);
          MainAppController.setSnackbarPositivity(true);
          MainAppController.setSnackbarTimeout(5000);
          MainAppController.setSnackbarBackgroundColor("#E3FCEF");
          MainAppController.setSnackbarIconLeft("success-status");
          fetchLoginApproval();
          MainAppController.setSnackbar(true);
        });
      } catch (error) {
        const err = error as AxiosError<any>;        
        if(err.response?.data.message){
          MainAppController.showMessageModal(
            new ModalMessageEntities({
              image: "Ill_Badge_Login_Failed",
              title: "Gagal diproses",
              message: `${err.response?.data?.message.id}`,
              textSuccess: "OKE",
              onSubmit: () => {
                fetchLoginApproval();
                MainAppController.closeMessageModal();
              }
            })
          );
        } else {
          MainAppController.showMessageModal(
            new ModalMessageEntities({
              image: "new-badge-failed",
              title: "Gagal diproses",
              message: "Terjadi kendala pada sistem. Silakan coba kembali beberapa saat lagi.",
              textSuccess: "Coba lagi",
              textCancel: "Kembali",
              onClose: () => {
                fetchLoginApproval();
                MainAppController.closeMessageModal();
              },
              onSubmit: () => {
                MainAppController.closeMessageModal();
                confirmRequestById(item, reviewType);
              }
            })
          );
        }
      } finally {
        MainAppController.closeLoading();
      }
    }

    const handleDelete = (item: LoginApprovalDevice) => {
      MainAppController.showMessageModal(new ModalMessageEntities({
        image: "badge-confirmation-general",
        title: "Hapus dari perangkat terdaftar?",
        message: `<b>${item.accountName} - ${item.deviceName} (${item.deviceOs})</b> <br> akan dihapus dari perangkat & perlu konfirmasi ulang untuk mengakses kembali.`,
        textSuccess: "Ya, Hapus",
        textCancel: "Batalkan",
        onClose: () => {
          fetchLoginApproval();
          MainAppController.closeMessageModal();
        },
        onSubmit: () => {
          MainAppController.closeMessageModal();
          confirmRequestById(item, "reject");
        }
      }));
    };

    // --- table --- //
    const columns = [
      {
        name: "No",
        styleHead: "w-10 text-left whitespace-no-wrap text-sm font-bold text-black-lp-200",
        render: (item: LoginApprovalDevice, index: number) => {
          return `<div class='text-left text-sm text-black-lp-200 font-normal flex justify-center break-words line-clamp-2'>${index +
            1 +
            pagination.value.limit * (pagination.value.page - 1)}</div>`;
        }
      },
      {
        name: "Nama",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalDevice) => {
          return `
          <div class="flex flex-col gap-1">
            <div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.accountName}</div>
            <div class="overflow-ellipsis text-sm text-gray-lp-600 font-normal break-words line-clamp-2">${item.roleName}</div>
          </div>`;
        }
      },
      {
        name: "Nama device",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalDevice) => {
          return `
          <div class="flex flex-col gap-1">
            <div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.deviceName}</div>
            <div class="overflow-ellipsis text-sm text-gray-lp-600 font-normal break-words line-clamp-2">${item.deviceOs}</div>
          </div>`;
        }
      },
      {
        name: "Tanggal diterima",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalDevice) => {
          return `<div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">
          ${formatDateBasedTimezoneValue(item.approvedAt, "DD MMMM YYYY, HH:mm")}</div>`;
        }
      },
      {
        name: "Terakhir aktif",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalDevice) => {
          return `<div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">
          ${formatDateBasedTimezoneValue(item.lastActiveAt, "DD MMMM YYYY, HH:mm")}</div>`;
        }
      },
      {
        name: "Kode",
        styleHead: "w-18 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalDevice) => {
          return `<div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.pairingCode}</div>`;
        }
      },
      {
        name: "Atur",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        key: "button_column",
        styleButton: (item: LoginApprovalDevice) => ({
          outline: false,
          icon: "trash-white",
          sizeIcon: "18px",
          textColor: "white",
          color: "red-lp-100",
          title: "Hapus",
          styleButton: "flex flex-row-reverse",
          clickFunction: () => handleDelete(item),
        })
      },
    ];

    onMounted(() => {
       fetchLoginApproval();
    });

    // executing fetching data
    watch(
      () => ManagementLoginApprovalController.refreshList,
      (newVal) => {
        if (newVal) {
          pagination.value.page = 1;
          fetchLoginApproval();
        }
      }
    );

    watch(
      [search, startDate, endDate],
      () => {
        pagination.value.page = 1;
        fetchLoginApproval();
      }
    );

    return {
      // --- search --- //
      search,
      onSearch,
      clearSearch,

      // --- date --- //
      startDate,
      endDate,
      setDateRange,
      getFormattedDate,

      // --- data --- //
      loginApprovalDevicesList,

      // --- table --- //
      columns,
      pagination,
      isLoading,
      errorCause,

      // --- modal --- //
      handleDelete,
      fetchLoginApproval
    };
  }
});
